import { useState, useRef, useEffect, useCallback } from 'react';
import { Scissors, Play, Pause } from 'lucide-react';
import { Button } from '../ui/button';

export default function VideoTrimPanel({ videoUrl, onTrimChange }) {
  const videoRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [dragging, setDragging] = useState(null); // 'start' | 'end' | null
  const trackRef = useRef(null);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onLoaded = () => {
      setDuration(vid.duration);
      setTrimEnd(vid.duration);
      onTrimChange?.({ start: 0, end: vid.duration });
    };
    const onTime = () => setCurrentTime(vid.currentTime);
    vid.addEventListener('loadedmetadata', onLoaded);
    vid.addEventListener('timeupdate', onTime);
    return () => {
      vid.removeEventListener('loadedmetadata', onLoaded);
      vid.removeEventListener('timeupdate', onTime);
    };
  }, [videoUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePlay = () => {
    const vid = videoRef.current;
    if (!vid) return;
    if (playing) { vid.pause(); } else {
      vid.currentTime = trimStart;
      vid.play();
    }
    setPlaying(!playing);
  };

  // Stop at trim end
  useEffect(() => {
    const vid = videoRef.current;
    if (!vid || !playing) return;
    if (currentTime >= trimEnd) {
      vid.pause();
      vid.currentTime = trimStart;
      setPlaying(false);
    }
  }, [currentTime, trimEnd, trimStart, playing]);

  const formatTime = (t) => {
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleTrackClick = useCallback((e) => {
    if (!trackRef.current || !duration) return;
    const rect = trackRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const time = pct * duration;
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  }, [duration]);

  const handleDrag = useCallback((e) => {
    if (!dragging || !trackRef.current || !duration) return;
    const rect = trackRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const time = pct * duration;
    if (dragging === 'start') {
      const ns = Math.min(time, trimEnd - 1);
      setTrimStart(Math.max(0, ns));
      onTrimChange?.({ start: Math.max(0, ns), end: trimEnd });
    } else {
      const ne = Math.max(time, trimStart + 1);
      setTrimEnd(Math.min(duration, ne));
      onTrimChange?.({ start: trimStart, end: Math.min(duration, ne) });
    }
  }, [dragging, duration, trimStart, trimEnd, onTrimChange]);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => handleDrag(e);
    const onUp = () => setDragging(null);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', (e) => handleDrag(e.touches[0]));
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [dragging, handleDrag]);

  const startPct = duration ? (trimStart / duration) * 100 : 0;
  const endPct = duration ? (trimEnd / duration) * 100 : 100;
  const currentPct = duration ? (currentTime / duration) * 100 : 0;
  const trimDuration = trimEnd - trimStart;

  return (
    <div className="p-3 space-y-3" data-testid="video-trim-panel">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Scissors className="w-4 h-4 text-violet-400" />
        <span>Trim Video</span>
        <span className="ml-auto text-xs text-muted-foreground">
          {formatTime(trimDuration)} selected
        </span>
      </div>

      {/* Hidden video for time tracking */}
      <video ref={videoRef} src={videoUrl} className="hidden" preload="metadata" />

      {/* Timeline track */}
      <div className="relative h-12 bg-muted/30 rounded-lg overflow-hidden select-none" ref={trackRef}>
        {/* Trimmed region */}
        <div
          className="absolute top-0 bottom-0 bg-violet-500/20 border-y border-violet-500/40"
          style={{ left: `${startPct}%`, width: `${endPct - startPct}%` }}
        />

        {/* Current time indicator */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white z-10"
          style={{ left: `${currentPct}%` }}
        />

        {/* Start handle */}
        <div
          className="absolute top-0 bottom-0 w-3 bg-violet-500 cursor-col-resize z-20 rounded-l flex items-center justify-center"
          style={{ left: `${startPct}%` }}
          onMouseDown={() => setDragging('start')}
          onTouchStart={() => setDragging('start')}
          data-testid="trim-start-handle"
        >
          <div className="w-0.5 h-4 bg-white rounded-full" />
        </div>

        {/* End handle */}
        <div
          className="absolute top-0 bottom-0 w-3 bg-violet-500 cursor-col-resize z-20 rounded-r flex items-center justify-center"
          style={{ left: `${endPct}%`, transform: 'translateX(-100%)' }}
          onMouseDown={() => setDragging('end')}
          onTouchStart={() => setDragging('end')}
          data-testid="trim-end-handle"
        >
          <div className="w-0.5 h-4 bg-white rounded-full" />
        </div>

        {/* Click area */}
        <div className="absolute inset-0 z-0" onClick={handleTrackClick} />
      </div>

      {/* Time labels */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{formatTime(trimStart)}</span>
        <Button size="sm" variant="ghost" onClick={togglePlay} className="h-7 w-7 p-0" data-testid="trim-play-btn">
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
        </Button>
        <span>{formatTime(trimEnd)}</span>
      </div>
    </div>
  );
}
